if (!customElements.get('dc-product-details')) {
  customElements.define('dc-product-details', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.section = this.closest('.dc-product-redesign');
      this.note = this.querySelector('.dc-order-note');
      this.dialog = this.querySelector('dialog');
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(tab), options);
        tab.addEventListener('keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') target = (index + this.tabs.length - 1) % this.tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = this.tabs.length - 1;
          if (target !== undefined) {event.preventDefault(); this.select(this.tabs[target]); this.tabs[target].focus();}
        }, options);
      });
      this.querySelectorAll('[data-dc-expand]').forEach(button => button.addEventListener('click', () => {
        const panel = button.closest('[role="tabpanel"]');
        this.dialog.querySelector('h2').textContent = this.tabs.find(tab => tab.getAttribute('aria-controls') === panel.id).textContent.trim();
        this.dialog.querySelector('.dc-dialog-content').innerHTML = panel.querySelector('.dc-detail-content').innerHTML;
        this.dialog.showModal();
      }, options));
      this.querySelector('[data-dc-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {if (event.target === this.dialog) {const rect = this.dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();}}, options);
      this.note.addEventListener('input', () => {
        this.querySelector('[data-dc-note-count]').textContent = this.note.value.length;
        if (this.originalNote) {this.originalNote.value = this.note.value; this.originalNote.dispatchEvent(new Event('input', {bubbles: true})); this.originalNote.dispatchEvent(new Event('change', {bubbles: true}));}
      }, options);
      this.attachOriginalNote();
      this.observer = new MutationObserver(() => this.attachOriginalNote());
      this.observer.observe(this.section, {childList: true, subtree: true});
      const title = this.section.querySelector('.product__title h1');
      const badge = this.section.querySelector('[data-dc-wall-dimensions]');
      const match = title?.textContent.match(/(?:dim\.?\s*parete|parete)\s*:?\s*L\s*(\d+(?:[.,]\d+)?)\s*cm\s*[×x-]?\s*H\s*(\d+(?:[.,]\d+)?)\s*cm/i);
      if (badge && match) {badge.textContent = badge.dataset.label + ' ' + match[1] + ' × ' + match[2] + ' cm'; badge.hidden = false;}
      const found = new Set();
      this.querySelectorAll('.dc-detail-content a[href]').forEach(link => {
        let url;
        try {url = new URL(link.getAttribute('href'), window.location.origin);} catch {return;}
        if (!['http:', 'https:'].includes(url.protocol) || !/\.pdf$/i.test(url.pathname) || found.has(url.href)) return;
        found.add(url.href);
        const anchor = document.createElement('a'); anchor.href = url.href; anchor.target = '_blank'; anchor.rel = 'noopener';
        anchor.textContent = this.querySelector('[data-dc-pdf-label]').textContent.trim() + (found.size > 1 ? ' ' + found.size : '');
        this.querySelector('.dc-technical-links').append(anchor);
      });
      if (found.size || this.querySelector('[data-dc-download-family]')) this.querySelector('.dc-technical-empty').hidden = true;
      const pdfButton = this.querySelector('[data-dc-download-family]');
      if (pdfButton) pdfButton.addEventListener('click', async () => {
        const config = JSON.parse(this.querySelector('[data-dc-family-pdf-config]').textContent);
        const status = this.querySelector('.dc-family-pdf-status');
        pdfButton.disabled = true;
        status.textContent = status.dataset.dcLoading;
        try {
          const pieces = await Promise.all(config.urls.map(async url => {
            const response = await fetch(url);
            if (!response.ok) throw new Error('PDF part unavailable');
            const piece = await response.json();
            return piece.data;
          }));
          const binary = atob(pieces.join(''));
          const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
          if (bytes.byteLength !== config.bytes) throw new Error('PDF size mismatch');
          const digest = await crypto.subtle.digest('SHA-256', bytes);
          const hash = [...new Uint8Array(digest)].map(value => value.toString(16).padStart(2, '0')).join('');
          if (hash !== config.sha256) throw new Error('PDF checksum mismatch');
          const url = URL.createObjectURL(new Blob([bytes], {type: 'application/pdf'}));
          const link = document.createElement('a');
          link.href = url;
          link.download = config.filename;
          link.className = 'dc-detail-action';
          link.textContent = pdfButton.textContent;
          this.querySelector('.dc-family-pdf').append(link);
          pdfButton.hidden = true;
          this.pdfURL = url;
          link.click();
          status.textContent = status.dataset.dcSuccess;
        } catch {
          status.textContent = status.dataset.dcError;
        } finally {
          pdfButton.disabled = false;
        }
      }, options);
      const urlInput = this.querySelector('.dc-share-url');
      const status = this.querySelector('.dc-share-status');
      this.querySelector('[data-dc-copy]').addEventListener('click', async () => {
        try {await navigator.clipboard.writeText(urlInput.value); status.textContent = status.dataset.dcCopied;}
        catch {urlInput.select(); status.textContent = status.dataset.dcSelect;}
      }, options);
      if (navigator.share) {const share = this.querySelector('[data-dc-share]'); share.hidden = false; share.addEventListener('click', async () => {try {await navigator.share({title: title?.textContent.trim(), url: urlInput.value});} catch {}}, options);}
      this.resize = new ResizeObserver(() => this.checkOverflow());
      this.querySelectorAll('.dc-detail-content').forEach(content => this.resize.observe(content));
      this.checkOverflow();
    }
    select(tab) {
      this.tabs.forEach(item => item.setAttribute('aria-selected', String(item === tab)));
      this.panels.forEach(panel => panel.hidden = panel.id !== tab.getAttribute('aria-controls'));
      this.checkOverflow();
    }
    checkOverflow() {
      this.querySelectorAll('.dc-detail-panel:not([hidden]) .dc-detail-content').forEach(content => {
        const button = content.parentElement.querySelector('[data-dc-expand]');
        if (button) button.hidden = content.scrollHeight <= content.clientHeight + 2;
      });
    }
    attachOriginalNote() {
      if (this.originalNote?.isConnected) return;
      const candidates = this.section.querySelectorAll('.gpo-element input[type="text"], .gpo-element textarea');
      for (const input of candidates) {
        const group = input.closest('.gpo-element');
        const label = group.querySelector('label');
        if (!/note\s*eventuali/i.test(label?.textContent || input.name)) continue;
        this.originalNote = input;
        group.setAttribute('data-dc-original-note', '');
        input.setAttribute('form', this.dataset.formId);
        if (/^properties\[/.test(input.name)) this.note.removeAttribute('name');
        if (this.note.value) {input.value = this.note.value; input.dispatchEvent(new Event('input', {bubbles: true})); input.dispatchEvent(new Event('change', {bubbles: true}));}
        else {this.note.value = input.value; this.querySelector('[data-dc-note-count]').textContent = this.note.value.length;}
        break;
      }
    }
    disconnectedCallback() {if (this.pdfURL) URL.revokeObjectURL(this.pdfURL); this.abort?.abort(); this.observer?.disconnect(); this.resize?.disconnect(); this.initialized = false;}
  });
}
